/**
 * Custom script to override "Saving X%" labels in the Recharge Storefront Experience (Affinity) cart.
 * Target: Recharge Shadow DOM
 * Source of Truth: /cart.js (line item properties)
 */

(function () {
  const DEBUG = true;
  const log = (...args) => {
    if (DEBUG) {
      console.log("[CustomCartSavings]", ...args);
    }
  };

  let cartData = null;
  let isUpdating = false;

  async function fetchCartData() {
    try {
      const res = await fetch("/cart.js");
      cartData = await res.json();
      log("Cart data fetched successfully");
    } catch (err) {
      log("Error fetching cart", err);
    }
  }

  function updateRechargeCartSavings() {
    if (isUpdating) return;

    const shadowHost = document.querySelector("#recharge-storefront-experiences__cart-drawer");
    if (!shadowHost || !shadowHost.shadowRoot) return;

    if (!cartData) {
      fetchCartData().then(() => updateRechargeCartSavings());
      return;
    }

    const savingLabels = Array.from(
      shadowHost.shadowRoot.querySelectorAll(
        "div.text-xs.text-right.text-storefront-drawer-accent.font-semibold"
      )
    );

    if (savingLabels.length === 0) return;

    isUpdating = true;

    savingLabels.forEach((label, index) => {
      let itemTitle = "";
      let foundTitle = false;
      let depth = 0;
      let current = label;

      // Attempt to find the title element for this label's line item
      while (current && depth < 12) {
        const titleEl = current.parentElement?.querySelector('div[id^="rc-item-title-"]');
        if (titleEl) {
          itemTitle = titleEl.textContent.trim();
          foundTitle = true;
          break;
        }
        current = current.parentElement;
        depth++;
      }

      let targetItem = null;
      if (foundTitle) {
        targetItem = cartData.items.find(item =>
          item.product_title === itemTitle ||
          item.title.includes(itemTitle) ||
          item.variant_options.some(opt => opt.includes(itemTitle))
        );
      }

      // Fallback to index if title match fails
      if (!targetItem) {
        targetItem = cartData.items[index];
      }

      if (!targetItem) return;

      let savingPercent = targetItem.properties?._cart_saving_percent;

      // Special case for $0 (gift) items
      if (targetItem.price === 0 || targetItem.final_price === 0) {
        savingPercent = "100";
      }

      if (savingPercent) {
        const newText = `Saving ${savingPercent}%`;
        if (label.textContent !== newText) {
          log(`Updating label for "${targetItem.title}" to: ${newText} (was: ${label.textContent})`);
          label.textContent = newText;
          label.dataset.customUpdated = "true";
        }
      }
    });

    isUpdating = false;
  }

  let pollingInterval = null;
  function triggerPolling(duration = 5000, interval = 500) {
    log("Triggering polling for", duration, "ms");
    if (pollingInterval) clearInterval(pollingInterval);

    const startTime = Date.now();
    pollingInterval = setInterval(() => {
      if (Date.now() - startTime > duration) {
        clearInterval(pollingInterval);
        pollingInterval = null;
        return;
      }
      updateRechargeCartSavings();
    }, interval);
  }

  // Use a more robust observer
  const observer = new MutationObserver((mutations) => {
    let shouldUpdate = false;
    for (const mutation of mutations) {
      if (mutation.type === "childList" || mutation.type === "subtree" || mutation.type === "characterData") {
        shouldUpdate = true;
        break;
      }
    }
    if (shouldUpdate) {
      updateRechargeCartSavings();
    }
  });

  function startObserver() {
    const shadowHost = document.querySelector("#recharge-storefront-experiences__cart-drawer");
    if (shadowHost) {
      log("Shadow host found, starting observer");

      observer.observe(shadowHost, {
        childList: true,
        subtree: true,
        characterData: true
      });

      triggerPolling(); // Initial poll
      updateRechargeCartSavings();
    } else {
      setTimeout(startObserver, 1000);
    }
  }

  // Initialize
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startObserver);
  } else {
    startObserver();
  }

  // Listen for Shopify cart events
  const originalFetch = window.fetch;
  window.fetch = function () {
    return originalFetch.apply(this, arguments).then(async (response) => {
      const url = arguments[0];
      if (typeof url === 'string' && (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update'))) {
        log("Cart change detected via fetch, triggering polling...");
        await fetchCartData();
        triggerPolling();
      }
      return response;
    });
  };

  document.addEventListener("cart:updated", () => {
    fetchCartData().then(() => triggerPolling());
  });

  document.addEventListener("rebuy:cart:open", () => {
    fetchCartData().then(() => triggerPolling());
  });

  window.forceUpdateCartSavings = () => {
    fetchCartData().then(() => triggerPolling());
  };

})();

document.addEventListener('DOMContentLoaded', () => {
  const host = document.getElementById('recharge-storefront-experiences__cart-drawer');

  if (!host) {
    console.warn('Host element not found');
    return;
  }

  const shadowRoot = host.shadowRoot;

  if (!shadowRoot) {
    console.warn('shadowRoot not found');
    return;
  }

  let timeout = null;

  const observer = new MutationObserver(() => {
    clearTimeout(timeout);

    timeout = setTimeout(() => {
      const hiddenProducts = (window.hiddenOTPProducts || '')
        .split(',')
        .map(p => p.trim())
        .filter(Boolean);

      const items = shadowRoot.querySelectorAll(
        '.flex.flex-col.py-6.px-drawer.gap-4.border-storefront-drawer-border.border-b[role="listitem"]'
      );

      items.forEach((item) => {
        const titleEl = item.querySelector('.flex.flex-col .font-semibold');

        if (!titleEl) return;

        const title = titleEl.textContent.trim();

        if (hiddenProducts.includes(title)) {
          const select = item.querySelector('select');

          if (!select) return;

          const oneTimeOption = select.querySelector('option[value="ONETIME"]');

          if (oneTimeOption) {
            oneTimeOption.remove();
          }
        }
      });

    }, 150);
  });

  observer.observe(shadowRoot, {
    childList: true,
    subtree: true
  });
});